import { lazy, Suspense } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Route, Routes } from "react-router-dom"
import { withMenuBar } from "./Hocs/withMenuBar"
import { clearGlobalError } from "./Reducers/default-reducers/globalErrorReducer"
import { isAuthenticated } from "./Services/default/auth"
import { paths } from "./models/constantes"
import ErrorScreen from "./Screens/errorScreen"
import LinhaDePlanoDeControleFormV2 from "./Components/processo/LinhaDePlanoDeControleFormV2"

// 🚀 LAZY LOADING: Importação das factories apenas quando necessário
const HomeContainer = lazy(() => import("./Screens/Home"))
const LoginContainer = lazy(() => import("./factories/LoginFactory").then(m => ({ default: m.LoginContainer })))

// 📋 FORMULÁRIOS - Lazy Loading das Factories
const ProcessoFormContainer = lazy(() => import("./factories/ProcessoFormFactory").then(m => ({ default: m.ProcessoFormContainer })))
const OperacaoFormContainer = lazy(() => import("./factories/OperacaoFormFactory").then(m => ({ default: m.OperacaoFormContainer })))
const FluxoDeProcessoFormContainer = lazy(() => import("./factories/FluxoDeProcessoFormFactory").then(m => ({ default: m.FluxoDeProcessoFormContainer })))
const ParametroFormContainer = lazy(() => import("./factories/ParametroFormFactory").then(m => ({ default: m.ParametroFormContainer })))
const PlanoDeControleFormContainer = lazy(() => import("./factories/PlanoDeControleFormFactory").then(m => ({ default: m.PlanoDeControleFormContainer })))
const CadernoDeVerificacaoFormContainer = lazy(() => import("./factories/CadernoDeVerificacaoFormFactory").then(m => ({ default: m.CadernoDeVerificacaoFormContainer })))
const UnidadeFormContainer = lazy(() => import("./factories/UnidadeFormFactory").then(m => ({ default: m.UnidadeFormContainer })))
const RecursoFormContainer = lazy(() => import("./factories/RecursoFormFactory").then(m => ({ default: m.RecursoFormContainer })))
const AcaoFormContainer = lazy(() => import("./factories/AcaoFormFactory").then(m => ({ default: m.AcaoFormContainer })))
const MaterialFormContainer = lazy(() => import("./factories/MaterialFormFactory").then(m => ({ default: m.MaterialFormContainer })))
const SetorFormContainer = lazy(() => import("./factories/SetorFormFactory").then(m => ({ default: m.SetorFormContainer })))
const TurnoFormContainer = lazy(() => import("./factories/TurnoFormFactory").then(m => ({ default: m.TurnoFormContainer })))
const FrequenciaFormContainer = lazy(() => import("./factories/FrequenciaFormFactory").then(m => ({ default: m.FrequenciaFormContainer })))
const EstoqueFormContainer = lazy(() => import("./factories/EstoqueFormFactory").then(m => ({ default: m.EstoqueFormContainer })))
const RequisicaoMaterialFormContainer = lazy(() => import("./factories/RequisicaoMaterialFormFactory").then(m => ({ default: m.RequisicaoMaterialFormContainer })))
const RegraDeCorrecaoFormContainer = lazy(() => import("./factories/RegraDeCorrecaoFormFactory").then(m => ({ default: m.RegraDeCorrecaoFormContainer })))
const OrdemDeCorrecaoFormContainer = lazy(() => import("./factories/OrdemDeCorrecaoFormFactory").then(m => ({ default: m.OrdemDeCorrecaoFormContainer })))
const OrdemDeCorrecaoApontarContainer = lazy(() => import("./factories/OrdemDeCorrecaoApontarFactory").then(m => ({ default: m.OrdemDeCorrecaoApontarContainer })))

// 📊 VISUALIZAÇÕES/LISTAS - Lazy Loading das Factories
const ProcessosContainer = lazy(() => import("./factories/ProcessosFactory").then(m => ({ default: m.ProcessosContainer })))
const FluxosContainer = lazy(() => import("./factories/FluxosFactory").then(m => ({ default: m.FluxosContainer })))
const OperacoesContainer = lazy(() => import("./factories/OperacoesFactory").then(m => ({ default: m.OperacoesContainer })))
const ParametrosContainer = lazy(() => import("./factories/ParametrosFactory").then(m => ({ default: m.ParametrosContainer })))
const PlanosDeControleContainer = lazy(() => import("./factories/PlanosDeControleFactory").then(m => ({ default: m.PlanosDeControleContainer })))
const CadernosContainer = lazy(() => import("./factories/CadernosFactory").then(m => ({ default: m.CadernosContainer })))
const CadernoDeVerificacaoContainer = lazy(() => import("./factories/CadernoDeVerificacaoFactory").then(m => ({ default: m.CadernoDeVerificacaoContainer })))
const RecursosContainer = lazy(() => import("./factories/RecursosFactory").then(m => ({ default: m.RecursosContainer })))
const MateriaisContainer = lazy(() => import("./factories/MateriaisFactory").then(m => ({ default: m.MateriaisContainer })))
const UnidadesContainer = lazy(() => import("./factories/UnidadesFactory").then(m => ({ default: m.UnidadesContainer })))
const AcoesContainer = lazy(() => import("./factories/AcoesFactory").then(m => ({ default: m.AcoesContainer })))
const OrdenDeCorrecaoContainer = lazy(() => import("./factories/OrdenDeCorrecaoFactory").then(m => ({ default: m.OrdenDeCorrecaoContainer })))
const OrdenacaoDeFolhasContainer = lazy(() => import("./factories/OrdenacaoDeFolhasFactory").then(m => ({ default: m.OrdenacaoDeFolhasContainer })))

// 🔧 UTILITÁRIOS/DASHBOARDS - Lazy Loading das Factories  
const CepContainer = lazy(() => import("./factories/CepFactory").then(m => ({ default: m.CepContainer })))
const ReporteContainer = lazy(() => import("./factories/ReporteFactory").then(m => ({ default: m.ReporteContainer })))
const ProcessoDashboardContainer = lazy(() => import("./factories/ProcessoDashboardFactory").then(m => ({ default: m.ProcessoDashboardContainer })))
const MaterialConsumptionDashboardContainer = lazy(() => import("./factories/MaterialConsumptionDashboardFactory").then(m => ({ default: m.MaterialConsumptionDashboardContainer })))

const Router = () => {
	const dispatch = useDispatch()
	const globalError = useSelector((state) => state.globalError)
	const global = useSelector((state) => state.global)

	if (globalError) {
		return (
			<ErrorScreen
				errorInfo={globalError}
				onRetry={() => dispatch(clearGlobalError())}
			/>
		)
	}

	return (
		<Routes>
			{/* 🏠 ROTA PRINCIPAL */}
			<Route 
				path="/" 
				element={
					<Suspense fallback={<div>Carregando página inicial...</div>}>
						<HomeContainer />
					</Suspense>
				} 
			/>

			{/* 🔐 LOGIN */}
			<Route 
				path="/Login" 
				element={
					<Suspense fallback={<div>Carregando login...</div>}>
						<LoginContainer />
					</Suspense>
				} 
			/>

			{/* ===== FORMULÁRIOS ===== */}
			<Route 
				path="/processoForm" 
				element={
					<Suspense fallback={<div>Carregando formulário de processo...</div>}>
						<ProcessoFormContainer />
					</Suspense>
				} 
			/>
			<Route 
				path="/operacaoForm" 
				element={
					<Suspense fallback={<div>Carregando formulário de operação...</div>}>
						<OperacaoFormContainer />
					</Suspense>
				} 
			/>
			<Route 
				path="/fluxoDeProcessoForm" 
				element={
					<Suspense fallback={<div>Carregando formulário de fluxo...</div>}>
						<FluxoDeProcessoFormContainer />
					</Suspense>
				} 
			/>
			<Route 
				path="/parametroForm" 
				element={
					<Suspense fallback={<div>Carregando formulário de parâmetro...</div>}>
						<ParametroFormContainer />
					</Suspense>
				} 
			/>
			<Route 
				path="/planoDeControleForm" 
				element={
					<Suspense fallback={<div>Carregando formulário de plano...</div>}>
						<PlanoDeControleFormContainer />
					</Suspense>
				} 
			/>
			<Route 
				path="/cadernoDeVerificacaoForm" 
				element={
					<Suspense fallback={<div>Carregando formulário de caderno...</div>}>
						<CadernoDeVerificacaoFormContainer />
					</Suspense>
				} 
			/>
			<Route 
				path="/unidadeForm" 
				element={
					<Suspense fallback={<div>Carregando formulário de unidade...</div>}>
						<UnidadeFormContainer />
					</Suspense>
				} 
			/>
			<Route 
				path="/recursoForm" 
				element={
					<Suspense fallback={<div>Carregando formulário de recurso...</div>}>
						<RecursoFormContainer />
					</Suspense>
				} 
			/>
			<Route 
				path="/acaoForm" 
				element={
					<Suspense fallback={<div>Carregando formulário de ação...</div>}>
						<AcaoFormContainer />
					</Suspense>
				} 
			/>
			<Route 
				path="/materialForm" 
				element={
					<Suspense fallback={<div>Carregando formulário de material...</div>}>
						<MaterialFormContainer />
					</Suspense>
				} 
			/>
			<Route 
				path="/setorForm" 
				element={
					<Suspense fallback={<div>Carregando formulário de setor...</div>}>
						<SetorFormContainer />
					</Suspense>
				} 
			/>
			<Route 
				path="/turnoForm" 
				element={
					<Suspense fallback={<div>Carregando formulário de turno...</div>}>
						<TurnoFormContainer />
					</Suspense>
				} 
			/>
			<Route 
				path="/frequenciaForm" 
				element={
					<Suspense fallback={<div>Carregando formulário de frequência...</div>}>
						<FrequenciaFormContainer />
					</Suspense>
				} 
			/>
			<Route 
				path="/estoqueForm" 
				element={
					<Suspense fallback={<div>Carregando formulário de estoque...</div>}>
						<EstoqueFormContainer />
					</Suspense>
				} 
			/>
			<Route 
				path="/requisicaoMaterialForm" 
				element={
					<Suspense fallback={<div>Carregando formulário de requisição...</div>}>
						<RequisicaoMaterialFormContainer />
					</Suspense>
				} 
			/>
			<Route 
				path={paths.regraDeCorrecaoForm} 
				element={
					<Suspense fallback={<div>Carregando formulário de regra...</div>}>
						<RegraDeCorrecaoFormContainer />
					</Suspense>
				} 
			/>
			<Route 
				path={paths.ordemDeCorrecaoForm} 
				element={
					<Suspense fallback={<div>Carregando formulário de ordem...</div>}>
						<OrdemDeCorrecaoFormContainer />
					</Suspense>
				} 
			/>
			<Route 
				path={paths.ordemDeCorrecaoApontar} 
				element={
					<Suspense fallback={<div>Carregando apontamento de ordem...</div>}>
						<OrdemDeCorrecaoApontarContainer />
					</Suspense>
				} 
			/>

			{/* ===== VISUALIZAÇÕES/LISTAS ===== */}
			<Route 
				path="/processos" 
				element={
					<Suspense fallback={<div>Carregando lista de processos...</div>}>
						<ProcessosContainer />
					</Suspense>
				} 
			/>
			<Route 
				path="/fluxos" 
				element={
					<Suspense fallback={<div>Carregando lista de fluxos...</div>}>
						<FluxosContainer />
					</Suspense>
				} 
			/>
			<Route 
				path="/operacoes" 
				element={
					<Suspense fallback={<div>Carregando lista de operações...</div>}>
						<OperacoesContainer />
					</Suspense>
				} 
			/>
			<Route 
				path="/parametros" 
				element={
					<Suspense fallback={<div>Carregando lista de parâmetros...</div>}>
						<ParametrosContainer />
					</Suspense>
				} 
			/>
			<Route 
				path={paths.planosDeControle} 
				element={
					<Suspense fallback={<div>Carregando planos de controle...</div>}>
						<PlanosDeControleContainer />
					</Suspense>
				} 
			/>
			<Route 
				path={paths.linhaDePlanoDeControleForm} 
				element={
					<Suspense fallback={<div>Carregando planos de controle...</div>}>
						<LinhaDePlanoDeControleFormV2 />
					</Suspense>
				} 
			/>
			<Route 
				path={paths.cadernos} 
				element={
					<Suspense fallback={<div>Carregando cadernos...</div>}>
						<CadernosContainer />
					</Suspense>
				} 
			/>
			<Route 
				path={`${paths.cadernoDeVerificacao}/:id`} 
				element={
					<Suspense fallback={<div>Carregando caderno de verificação...</div>}>
						<CadernoDeVerificacaoContainer />
					</Suspense>
				} 
			/>
			<Route 
				path={paths.recursos} 
				element={
					<Suspense fallback={<div>Carregando recursos...</div>}>
						<RecursosContainer />
					</Suspense>
				} 
			/>
			<Route 
				path={paths.materiais} 
				element={
					<Suspense fallback={<div>Carregando materiais...</div>}>
						<MateriaisContainer />
					</Suspense>
				} 
			/>
			<Route 
				path={paths.unidades} 
				element={
					<Suspense fallback={<div>Carregando unidades...</div>}>
						<UnidadesContainer />
					</Suspense>
				} 
			/>
			<Route 
				path={paths.acoes} 
				element={
					<Suspense fallback={<div>Carregando ações...</div>}>
						<AcoesContainer />
					</Suspense>
				} 
			/>
			<Route 
				path={paths.ordensDeCorrecao} 
				element={
					<Suspense fallback={<div>Carregando ordens de correção...</div>}>
						<OrdenDeCorrecaoContainer />
					</Suspense>
				} 
			/>
			<Route 
				path={paths.ordenacaoDeFolhas} 
				element={
					<Suspense fallback={<div>Carregando ordenação de folhas...</div>}>
						<OrdenacaoDeFolhasContainer />
					</Suspense>
				} 
			/>

			{/* ===== UTILITÁRIOS/DASHBOARDS ===== */}
			<Route 
				path="/cep" 
				element={
					<Suspense fallback={<div>Carregando consulta CEP...</div>}>
						<CepContainer />
					</Suspense>
				} 
			/>
			<Route 
				path={paths.reporte} 
				element={
					<Suspense fallback={<div>Carregando relatório...</div>}>
						<ReporteContainer />
					</Suspense>
				} 
			/>
			<Route 
				path={paths.analisePerformanceCp} 
				element={
					<Suspense fallback={<div>Carregando dashboard...</div>}>
						<ProcessoDashboardContainer />
					</Suspense>
				} 
			/>
			<Route 
				path={paths.materialConsumptionDashboard} 
				element={
					<Suspense fallback={<div>Carregando dashboard de consumo...</div>}>
						<MaterialConsumptionDashboardContainer />
					</Suspense>
				} 
			/>
		</Routes>
	)
}

export default withMenuBar(Router)
